import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  applyAction,
  continueGame,
  createGame,
  eventsFor,
  firstRollEvents,
  discardOwed,
  legalActions,
  abandonOffer,
  botAwaitingTrade,
  botMoves,
  botMovesAt,
  type BotKind,
  type BotMove,
  turnKey,
  viewFor,
  type Action,
  type ActionResult,
  type BoardLayout,
  type GameEvent,
  type GameState,
  type HouseRules,
} from '../engine';
import type { GameClient, LogEntry } from './game-client';
import { logEntry } from './log';

const STORAGE_KEY = 'hexagon.local.v1';
const UNDO_LIMIT = 20;

export interface LocalGameSetup {
  names: string[];
  layout: BoardLayout;
  seed: number;
  /** Cover the screen between seats so players don't see each other's hands. */
  privacy: boolean;
  /** Soft turn timer in seconds; optional so older saves still load. */
  turnSeconds?: number | null;
  /** Optional so older saves still load (the game's config has the rules it was created with). */
  rules?: HouseRules;
  victoryPoints?: number;
  mapSize?: number | 'auto';
  /** Seats the computer plays. Optional so older saves still load. */
  cpu?: boolean[];
  /** Strategy per CPU seat; seats missing here (older saves) play 'basic'. */
  bots?: BotKind[];
  /** Palette index per seat; older saves use the palette in seat order. */
  colors?: number[];
  /** A seat, or 'roll'. Older saves start with seat 0. */
  firstPlayer?: number | 'roll';
}

/** Pause between CPU beats so people can follow them. */
const BOT_DELAY_MS = 350;
/** How long a CPU's trade offer waits for the humans at the table before it's withdrawn. */
const TRADE_PATIENCE_MS = 20_000;

export interface LocalGameSave {
  setup: LocalGameSetup;
  state: GameState;
  log: LogEntry[];
  /** Earlier states, newest last, for undo. Optional so older saves still load. */
  undo?: { state: GameState; logLength: number }[];
}

/**
 * Whose view the screen shows. A human who owes something comes first; while
 * the computer moves, the screen stays with `fallback` (the last human to hold
 * the device) so CPU hands stay hidden and nobody's asked to pass the device.
 */
export function actingSeat(state: GameState, cpu: readonly boolean[] = [], fallback = 0): number {
  const human = (i: number) => !cpu[i];
  if (state.phase.kind === 'discard') {
    const owing = state.phase.owed.findIndex((n, i) => n > 0 && human(i));
    if (owing >= 0) return owing;
  }
  if (human(state.currentPlayer)) return state.currentPlayer;
  if (human(fallback)) return fallback;
  const first = state.players.findIndex((_, i) => human(i));
  return first >= 0 ? first : state.currentPlayer;
}

export function newLocalGame(setup: LocalGameSetup): LocalGameSave {
  const state = createGame({
    playerCount: setup.names.length,
    seed: setup.seed,
    layout: setup.layout,
    ...(setup.rules ? { rules: setup.rules } : {}),
    ...(setup.victoryPoints ? { victoryPointsToWin: setup.victoryPoints } : {}),
    ...(setup.mapSize ? { mapSize: setup.mapSize } : {}),
    ...(setup.firstPlayer !== undefined ? { firstPlayer: setup.firstPlayer } : {}),
  });
  const first = state.firstPlayer ?? 0;
  const rolled = firstRollEvents(state).flatMap((e, i) => logEntry(e, setup.names, i) ?? []);
  return { setup, state, log: [...rolled, { id: rolled.length, text: `Game started. ${setup.names[first]} places first.` }] };
}

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null;

/**
 * A save from an older build, a half-written one, or one edited by hand would
 * otherwise crash /local with no way back to the setup form. Shape-check it,
 * then make sure the engine can actually read the state.
 */
export function parseLocalSave(raw: string): LocalGameSave | null {
  let save: unknown;
  try {
    save = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(save) || !isObject(save.setup) || !isObject(save.state) || !Array.isArray(save.log)) return null;
  const { setup, state } = save;
  const names = setup.names;
  if (!Array.isArray(names) || !names.every((n) => typeof n === 'string')) return null;
  if (!Array.isArray(state.players) || state.players.length !== names.length) return null;
  try {
    const game = state as unknown as GameState;
    viewFor(game, 0);
    legalActions(game, game.currentPlayer);
  } catch {
    return null;
  }
  const ok = save as unknown as LocalGameSave;
  // Undo is a convenience; a damaged stack is dropped rather than failing the whole save.
  return Array.isArray(ok.undo) ? ok : { ...ok, undo: [] };
}

export function loadLocalGame(): LocalGameSave | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseLocalSave(raw) : null;
  } catch {
    return null;
  }
}

export function saveLocalGame(save: LocalGameSave | null): void {
  try {
    if (!save) return localStorage.removeItem(STORAGE_KEY);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
    } catch {
      // Over quota: the undo stack holds up to 20 full states, so it goes first.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...save, undo: [] }));
    }
  } catch {
    // Storage full or blocked; the game still works, it just won't survive a refresh.
  }
}

/** A game with no human seats is watched, not played: the screen shows the spectator view. */
export function watchOnly(setup: Pick<LocalGameSetup, 'names' | 'cpu'>): boolean {
  return setup.names.every((_, i) => !!setup.cpu?.[i]);
}

export function useLocalGame(initial: LocalGameSave): GameClient & { save: LocalGameSave } {
  const [save, setSave] = useState(initial);
  const { state, setup, log } = save;
  const cpu = setup.cpu ?? [];
  const watching = watchOnly(setup);
  const lastHuman = useRef(0);
  const acting = actingSeat(state, cpu, lastHuman.current);
  if (!cpu[acting]) lastHuman.current = acting;
  const seat = watching ? null : acting;

  const view = useMemo(() => viewFor(state, seat), [state, seat]);
  const legal = useMemo(() => (seat === null ? [] : legalActions(state, seat)), [state, seat]);

  const act = (action: Action, as = seat ?? acting): string | null => commit(applyAction(state, as, action));

  const commit = (result: ActionResult): string | null => (result.ok ? (commitAll([result]), null) : result.error);

  /** Saves one or more results applied in sequence as a single undo step. */
  const commitAll = (results: { state: GameState; events: GameEvent[] }[]) => {
    const last = results[results.length - 1];
    if (!last) return;
    let nextId = (log[log.length - 1]?.id ?? 0) + 1;
    const lines = results.flatMap((r) =>
      eventsFor(r.events, null, r.state).flatMap((e) => {
        const entry = logEntry(e, setup.names, nextId);
        if (!entry) return [];
        nextId++;
        return [entry];
      }),
    );
    const nextLog = [...log, ...lines];
    const undo = [...(save.undo ?? []), { state, logLength: log.length }].slice(-UNDO_LIMIT);
    const next = { setup, state: last.state, log: nextLog, undo };
    saveLocalGame(next);
    setSave(next);
  };

  const playAll = (moves: BotMove[]) => {
    const results: { state: GameState; events: GameEvent[] }[] = [];
    let s = state;
    for (const m of moves) {
      const r = applyAction(s, m.seat, m.action);
      if (!r.ok) break;
      results.push(r);
      s = r.state;
    }
    commitAll(results);
  };

  // Turn timer: when a human's turn runs out, the computer finishes it for them.
  const key = turnKey(state);
  const [autopilot, setAutopilot] = useState<string | null>(null);
  const [info, setInfo] = useState<{ id: number; text: string } | null>(null);
  useEffect(() => {
    const seconds = setup.turnSeconds;
    if (!seconds || state.phase.kind === 'gameOver' || cpu[state.currentPlayer]) return;
    const t = setTimeout(() => {
      setAutopilot(key);
      setInfo({ id: Date.now(), text: `Time's up: the computer is finishing ${setup.names[state.currentPlayer]}'s turn.` });
    }, seconds * 1000);
    return () => clearTimeout(t);
  }, [key, setup.turnSeconds]);

  const offerSeen = useRef<{ id: number; at: number } | null>(null);
  // Let the computer play whatever it owes, a beat at a time (all owed discards together, a setup settlement with its road).
  useEffect(() => {
    const onAutopilot = autopilot === key;
    if (!cpu.some(Boolean) && !onAutopilot) return;
    // A timed-out human's turn is finished by the basic CPU, which never offers trades on their behalf.
    const botOf = (i: number): BotKind | null =>
      cpu[i] ? (setup.bots?.[i] ?? 'basic') : onAutopilot && (i === state.currentPlayer || discardOwed(state, i) > 0) ? 'basic' : null;
    const moves = botMoves(state, botOf);
    const now = Date.now();
    if (state.trade && offerSeen.current?.id !== state.trade.id) offerSeen.current = { id: state.trade.id, at: now };
    if (moves.length > 0) {
      const at = botMovesAt(state, moves, botOf, now, BOT_DELAY_MS, offerSeen.current?.at ?? now);
      const t = setTimeout(() => playAll(moves), Math.max(0, at - now));
      return () => clearTimeout(t);
    }
    if (!botAwaitingTrade(state, botOf)) return;
    const t = setTimeout(() => playAll([abandonOffer(state)!]), TRADE_PATIENCE_MS);
    return () => clearTimeout(t);
  }, [state, autopilot]);

  const stack = save.undo ?? [];
  const undo = () => {
    const last = stack[stack.length - 1];
    if (!last) return;
    const next = { setup, state: last.state, log: log.slice(0, last.logLength), undo: stack.slice(0, -1) };
    saveLocalGame(next);
    setSave(next);
  };

  return {
    view,
    seat,
    legal,
    names: setup.names,
    log,
    act,
    save,
    hotSeat: true,
    handOf: (s: number) => state.players[s]!.resources,
    turnSeconds: setup.turnSeconds ?? null,
    cpu,
    bots: setup.names.map((_, i) => (cpu[i] ? (setup.bots?.[i] ?? 'basic') : undefined)),
    colors: setup.colors ?? setup.names.map((_, i) => i),
    info,
    continueTo: (target: number) => void commit(continueGame(state, target)),
    ...(stack.length > 0 ? { undo } : {}),
  };
}
